import * as React from 'react';
// `React` must stay IN SCOPE even though nothing here names it: knowit-main compiles this package
// with the CLASSIC JSX transform (`"jsx": "react"`, React 17), where every JSX element expands to
// `React.createElement`. slate and coil use `"jsx": "react-jsx"`, where the same import is unused and
// `noUnusedLocals` rejects it — which is why slate consumed this package through a hand-written
// vendor-types shim rather than compiling it. `void React` satisfies both: the binding is used, and
// it is still in scope for the classic transform. Deleting either line breaks knowit-main's build.
void React;

import type { StatData } from '../types';
import { fmt } from './charts';

/**
 * One KPI. Two honesty rules with teeth:
 *
 * - `value: null` renders "not recorded" — a stat tile is the easiest place to fake a number,
 *   so it is the place absence must be loudest.
 * - A delta only renders COLORED when `deltaBasis` says what it is against. A bare green arrow
 *   is a claim ("this improved") with no referent; without a basis the delta shows neutral.
 */
export function Stat({ stat }: { stat: StatData }) {
  const hasBasis = Boolean(stat.deltaBasis && stat.deltaBasis.trim());
  const delta = stat.delta ?? null;
  const deltaColor =
    delta === null || !hasBasis ? '#6b7280' : delta >= 0 ? '#2f9e6e' : '#c2544d';

  return (
    <div className="docit-widget-stat" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.02em', opacity: 0.7 }}>
        {stat.label}
      </span>
      <span style={{ fontSize: 26, fontWeight: 600, lineHeight: 1.1 }}>
        {stat.value === null || stat.value === undefined
          ? 'not recorded'
          : typeof stat.value === 'number'
            ? fmt(stat.value, stat.unit)
            : `${stat.value}${stat.unit ?? ''}`}
      </span>
      {delta !== null && (
        <span style={{ fontSize: 12, color: deltaColor }}>
          {delta >= 0 ? '▲' : '▼'} {fmt(Math.abs(delta), stat.unit)}
          {hasBasis ? ` ${stat.deltaBasis}` : ' (no basis stated)'}
        </span>
      )}
    </div>
  );
}
